import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { playChime } from '../lib/tabAlerts';
import { isSupportSoundMuted } from '../utils/supportPing';

const NEW_PLAYER_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Watches the admin `users` array for brand-new signups and fires a soft
 * audio chime + desktop notification (when the tab is hidden) the first
 * time each user_id is seen. The very first poll is a no-op so we don't
 * alarm on initial page load.
 *
 * Returns `requestPermission` so the admin can opt in once from the UI.
 */
export const useNewSignupAlerts = (users) => {
    const seenIdsRef = useRef(new Set());
    const initializedRef = useRef(false);

    useEffect(() => {
        if (!Array.isArray(users) || users.length === 0) return;

        // First pass: seed the "seen" set from existing players so we only
        // toast for true new arrivals after the page is open.
        if (!initializedRef.current) {
            users.forEach((u) => seenIdsRef.current.add(u.id));
            initializedRef.current = true;
            return;
        }

        const now = Date.now();
        const fresh = users.filter((u) => {
            if (!u || u.role !== 'user' || !u.id) return false;
            if (seenIdsRef.current.has(u.id)) return false;
            if (!u.created_at) return false;
            const created = new Date(u.created_at).getTime();
            if (Number.isNaN(created)) return false;
            return now - created <= NEW_PLAYER_WINDOW_MS;
        });

        if (fresh.length === 0) return;

        fresh.forEach((u) => {
            seenIdsRef.current.add(u.id);
            toast.success(`🎉 New player joined: ${u.name}`, {
                description: u.phone ? `Phone ${u.phone}` : u.email,
                duration: 8000,
            });
            // Native browser notification when the tab is hidden / unfocused
            if (typeof document !== 'undefined' && document.hidden &&
                'Notification' in window && Notification.permission === 'granted') {
                try {
                    new Notification('New BETohalics signup', {
                        body: `${u.name} just created an account`,
                        icon: '/favicon.ico',
                        tag: 'signup-' + u.id,
                    });
                } catch { /* ignore */ }
            }
        });

        if (!isSupportSoundMuted()) {
            playChime();
        }
    }, [users]);

    const requestPermission = () => {
        if (typeof window === 'undefined' || !('Notification' in window)) return;
        if (Notification.permission === 'default') {
            Notification.requestPermission().catch(() => { /* ignore */ });
        }
    };

    return { requestPermission };
};
